type TrustedTypesPolicy = {
  createScriptURL(input: string): unknown;
  createHTML(input: string): unknown;
  createScript(input: string): unknown;
};

type TrustedTypesFactory = {
  createPolicy(
    name: string,
    rules: {
      createScriptURL(input: string): string;
      createHTML(input: string): string;
      createScript(input: string): string;
    },
  ): TrustedTypesPolicy;
};

declare global {
  interface Window {
    trustedTypes?: TrustedTypesFactory;
  }
}

export function isAllowedScriptUrl(
  input: string,
  allowedOrigins: ReadonlySet<string>,
  baseUrl: string,
) {
  try {
    const url = new URL(input, baseUrl);
    const baseOrigin = new URL(baseUrl).origin;
    return allowedOrigins.has(url.origin)
      && (url.protocol === "https:" || url.origin === baseOrigin);
  } catch {
    return false;
  }
}

export function configuredScriptOrigins() {
  const origins = new Set<string>([
    window.location.origin,
    "https://forge.butterfly-effect.dev",
    "https://widgets.leadconnectorhq.com",
    "https://stcdn.leadconnectorhq.com",
    "https://challenges.cloudflare.com",
  ]);
  const configuredUrls = [
    import.meta.env.VITE_ANALYTICS_ENDPOINT,
    import.meta.env.VITE_FRONTEND_FORGE_API_URL,
  ];

  for (const configuredUrl of configuredUrls) {
    if (!configuredUrl) continue;
    try {
      origins.add(new URL(configuredUrl, window.location.href).origin);
    } catch {
      // Invalid optional integration URLs remain blocked.
    }
  }

  return origins;
}

let scriptPolicy: TrustedTypesPolicy | undefined;

export function installScriptSecurityPolicy() {
  if (!window.trustedTypes || scriptPolicy) return;
  const allowedOrigins = configuredScriptOrigins();

  try {
    scriptPolicy = window.trustedTypes.createPolicy("default", {
      createScriptURL(input) {
        if (!isAllowedScriptUrl(input, allowedOrigins, window.location.href)) {
          throw new TypeError(`Blocked untrusted script URL: ${input}`);
        }
        return new URL(input, window.location.href).href;
      },
      // Script SRC loading is the real security boundary (gated above by an
      // explicit origin allowlist). HTML/script-text sinks below are left
      // permissive: they're hit by already-loaded, already-trusted vendor
      // bundles (React's own DOM updates, the LeadConnector widget's
      // internal rendering) doing routine innerHTML/textContent-equivalent
      // work, not by any string this codebase feeds from user input. A
      // "default" Trusted Types policy without these two throws on any such
      // assignment with no way to opt in per-call — that's what caused the
      // production outage this policy was patched around once already
      // (React 19's script-hoisting used innerHTML) and, separately, blocked
      // the chat widget's phone-input formatting (intlTelInput.min.js).
      createHTML(input) {
        return input;
      },
      createScript(input) {
        return input;
      },
    });
  } catch {
    // A host platform may have installed the same named policy first.
  }
}

export function appendTrustedScript(
  url: string,
  configure?: (script: HTMLScriptElement) => void,
) {
  const allowedOrigins = configuredScriptOrigins();
  if (!isAllowedScriptUrl(url, allowedOrigins, window.location.href)) {
    throw new TypeError(`Blocked untrusted script URL: ${url}`);
  }

  const script = document.createElement("script");
  const trustedUrl = scriptPolicy?.createScriptURL(url) ?? url;
  script.src = trustedUrl as string;
  script.async = true;
  const nonce = document.querySelector<HTMLScriptElement>("script[nonce]")?.nonce;
  if (nonce) script.nonce = nonce;
  configure?.(script);
  document.head.appendChild(script);
  return script;
}
